import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/page-header";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Section, Field, ActionBar } from "@/components/form-shell";

const EMPTY = {
  salutation: "", firstName: "", lastName: "", company: "", title: "",
  email: "", secondaryEmail: "", phone: "", mobile: "", website: "", skypeId: "", linkedinUrl: "",
  source: "", status: "NEW", score: "0",
  annualRevenue: "", leadIndustry: "", leadDegree: "", leadGeography: "",
  solutions: "", cbcOfferings: "", emailResponse: "", approach: "",
  leadDate: "", campaignId: "", notes: "",
  // Zoho-style additional
  fax: "", noOfEmployees: "", emailOptOut: false,
  street: "", city: "", state: "", zip: "", country: "",
};
type LeadForm = typeof EMPTY;

export default function LeadNewPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [campaigns, setCampaigns] = useState<{ id: string; name: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<LeadForm>(EMPTY);

  useEffect(() => {
    api.get("/campaigns").then((r) => setCampaigns(r.data.data ?? []));
  }, []);

  function set<K extends keyof LeadForm>(k: K, v: LeadForm[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function save(mode: "save" | "saveAndNew") {
    if (!(form.firstName || form.lastName || form.company)) {
      setError("Provide a first name, last name, or company.");
      return;
    }
    setBusy(true); setError(null);
    try {
      const payload: any = {};
      for (const [k, v] of Object.entries(form)) {
        if (v === "" || v === null || v === undefined) continue;
        if (["annualRevenue", "score"].includes(k)) payload[k] = Number(v);
        else payload[k] = v;
      }
      await api.post("/leads", payload);
      qc.invalidateQueries({ queryKey: ["/leads"] });
      if (mode === "saveAndNew") { setForm(EMPTY); setError(null); }
      else navigate("/leads");
    } catch (err: any) {
      setError(err?.response?.data?.message ?? "Failed to create lead.");
    } finally { setBusy(false); }
  }

  const canSubmit = !!(form.firstName || form.lastName || form.company);

  return (
    <>
      <PageHeader title="Create Lead" description="Zoho-style lead capture." />
      <form onSubmit={(e) => { e.preventDefault(); save("save"); }} className="p-6">
        <div className="mx-auto max-w-4xl space-y-4">
          <Section title="Lead Information">
            <Field id="salutation" label="Salutation">
              <Select id="salutation" value={form.salutation} onChange={(e) => set("salutation", e.target.value)}>
                <option value="">—</option>
                <option value="Mr.">Mr.</option>
                <option value="Ms.">Ms.</option>
                <option value="Mrs.">Mrs.</option>
                <option value="Dr.">Dr.</option>
                <option value="Prof.">Prof.</option>
              </Select>
            </Field>
            <Field id="title" label="Title"><Input id="title" value={form.title} onChange={(e) => set("title", e.target.value)} /></Field>
            <Field id="firstName" label="First Name"><Input id="firstName" value={form.firstName} onChange={(e) => set("firstName", e.target.value)} /></Field>
            <Field id="lastName" label="Last Name" required><Input id="lastName" value={form.lastName} onChange={(e) => set("lastName", e.target.value)} /></Field>
            <Field id="company" label="Company" required><Input id="company" value={form.company} onChange={(e) => set("company", e.target.value)} /></Field>
            <Field id="leadDate" label="Date"><Input id="leadDate" type="date" value={form.leadDate} onChange={(e) => set("leadDate", e.target.value)} /></Field>
          </Section>

          <Section title="Contact Channels">
            <Field id="email" label="Email"><Input id="email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} /></Field>
            <Field id="secondaryEmail" label="Secondary Email"><Input id="secondaryEmail" type="email" value={form.secondaryEmail} onChange={(e) => set("secondaryEmail", e.target.value)} /></Field>
            <Field id="phone" label="Phone"><Input id="phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} /></Field>
            <Field id="mobile" label="Mobile"><Input id="mobile" value={form.mobile} onChange={(e) => set("mobile", e.target.value)} /></Field>
            <Field id="website" label="Website"><Input id="website" type="url" placeholder="https://…" value={form.website} onChange={(e) => set("website", e.target.value)} /></Field>
            <Field id="skypeId" label="Skype ID"><Input id="skypeId" value={form.skypeId} onChange={(e) => set("skypeId", e.target.value)} /></Field>
            <Field id="linkedinUrl" label="LinkedIn Profile" className="md:col-span-2"><Input id="linkedinUrl" type="url" value={form.linkedinUrl} onChange={(e) => set("linkedinUrl", e.target.value)} /></Field>
          </Section>

          <Section title="Attribution & Scoring">
            <Field id="source" label="Lead Source">
              <Select id="source" value={form.source} onChange={(e) => set("source", e.target.value)}>
                <option value="">—</option>
                <option value="Cold Call">Cold Call</option>
                <option value="LinkedIn">LinkedIn</option>
                <option value="Apollo">Apollo</option>
                <option value="Webinar">Webinar</option>
                <option value="Event">Event</option>
                <option value="Partner">Partner</option>
                <option value="Website">Website</option>
                <option value="Referral">Referral</option>
                <option value="Email Campaign">Email Campaign</option>
              </Select>
            </Field>
            <Field id="status" label="Status">
              <Select id="status" value={form.status} onChange={(e) => set("status", e.target.value)}>
                <option value="NEW">New</option>
                <option value="CONTACTED">Contacted</option>
                <option value="QUALIFIED">Qualified</option>
                <option value="NURTURING">Nurturing</option>
                <option value="DISQUALIFIED">Disqualified</option>
              </Select>
            </Field>
            <Field id="score" label="Score (0–100)"><Input id="score" type="number" min="0" max="100" value={form.score} onChange={(e) => set("score", e.target.value)} /></Field>
            <Field id="campaignId" label="Campaign Source">
              <Select id="campaignId" value={form.campaignId} onChange={(e) => set("campaignId", e.target.value)}>
                <option value="">—</option>
                {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </Select>
            </Field>
            <Field id="annualRevenue" label="Annual Revenue (USD)"><Input id="annualRevenue" type="number" min="0" value={form.annualRevenue} onChange={(e) => set("annualRevenue", e.target.value)} /></Field>
          </Section>

          <Section title="Classification">
            <Field id="leadIndustry" label="Lead Industry"><Input id="leadIndustry" value={form.leadIndustry} onChange={(e) => set("leadIndustry", e.target.value)} /></Field>
            <Field id="leadDegree" label="Lead Degree">
              <Select id="leadDegree" value={form.leadDegree} onChange={(e) => set("leadDegree", e.target.value)}>
                <option value="">—</option>
                <option value="Hot">Hot</option>
                <option value="Warm">Warm</option>
                <option value="Cold">Cold</option>
              </Select>
            </Field>
            <Field id="leadGeography" label="Lead Geography">
              <Select id="leadGeography" value={form.leadGeography} onChange={(e) => set("leadGeography", e.target.value)}>
                <option value="">—</option>
                <option value="NA">North America</option>
                <option value="EMEA">EMEA</option>
                <option value="APAC">APAC</option>
                <option value="LATAM">LATAM</option>
                <option value="India">India</option>
              </Select>
            </Field>
            <Field id="solutions" label="Solutions"><Input id="solutions" placeholder="Data & AI, Cloud, Staffing" value={form.solutions} onChange={(e) => set("solutions", e.target.value)} /></Field>
            <Field id="cbcOfferings" label="CBC Offerings"><Input id="cbcOfferings" value={form.cbcOfferings} onChange={(e) => set("cbcOfferings", e.target.value)} /></Field>
            <Field id="emailResponse" label="Email Response">
              <Select id="emailResponse" value={form.emailResponse} onChange={(e) => set("emailResponse", e.target.value)}>
                <option value="">—</option>
                <option value="Opened">Opened</option>
                <option value="Clicked">Clicked</option>
                <option value="Replied">Replied</option>
                <option value="Bounced">Bounced</option>
                <option value="No Response">No Response</option>
              </Select>
            </Field>
            <Field id="approach" label="Approach" className="md:col-span-2">
              <Select id="approach" value={form.approach} onChange={(e) => set("approach", e.target.value)}>
                <option value="">—</option>
                <option value="Outbound">Outbound</option>
                <option value="Inbound">Inbound</option>
                <option value="Account-Based">Account-Based</option>
                <option value="Partner Intro">Partner Intro</option>
              </Select>
            </Field>
          </Section>

          <Section title="Address">
            <Field id="street" label="Street" className="md:col-span-2">
              <Input id="street" value={form.street} onChange={(e) => set("street", e.target.value)} />
            </Field>
            <Field id="city" label="City">
              <Input id="city" value={form.city} onChange={(e) => set("city", e.target.value)} />
            </Field>
            <Field id="state" label="State / region">
              <Input id="state" value={form.state} onChange={(e) => set("state", e.target.value)} />
            </Field>
            <Field id="zip" label="ZIP / postcode">
              <Input id="zip" value={form.zip} onChange={(e) => set("zip", e.target.value)} />
            </Field>
            <Field id="country" label="Country">
              <Input id="country" value={form.country} onChange={(e) => set("country", e.target.value)} />
            </Field>
          </Section>

          <Section title="Additional information">
            <Field id="fax" label="Fax">
              <Input id="fax" value={form.fax} onChange={(e) => set("fax", e.target.value)} />
            </Field>
            <Field id="noOfEmployees" label="No. of employees">
              <Input id="noOfEmployees" type="number" min="0" value={form.noOfEmployees} onChange={(e) => set("noOfEmployees", e.target.value)} />
            </Field>
            <div className="flex items-center gap-2 md:col-span-2">
              <input
                id="emailOptOut"
                type="checkbox"
                checked={form.emailOptOut}
                onChange={(e) => set("emailOptOut", e.target.checked)}
              />
              <label htmlFor="emailOptOut" className="text-sm font-medium leading-none">Email opt-out</label>
            </div>
          </Section>

          <Section title="Notes">
            <div className="md:col-span-2">
              <Label_ id="notes" />
              <textarea
                id="notes"
                className="flex min-h-[80px] w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                value={form.notes}
                onChange={(e) => set("notes", e.target.value)}
              />
            </div>
          </Section>

          <ActionBar
            busy={busy}
            canSubmit={canSubmit}
            onCancel={() => navigate("/leads")}
            onSaveAndNew={() => save("saveAndNew")}
            onSubmit={() => save("save")}
            error={error}
          />
        </div>
      </form>
    </>
  );
}

// Tiny local helper to keep the Notes area labelled without importing Label separately.
function Label_({ id }: { id: string }) {
  return <label htmlFor={id} className="text-sm font-medium leading-none">Notes</label>;
}
